/**
 * Box Tool
 *
 * Allows creating and editing box annotations.
 * Fully migrated from legacy VolumeTool + TransformationTool.
 *
 * Creation flow: click on point cloud → box placed at intersection, scaled by camera distance.
 * Editing flow: hover existing box → gizmo handles appear → drag to scale/translate/rotate.
 */

import * as THREE from 'three';
import { BaseTool, DragEventData } from '../../BaseTool';
import { Viewer } from '../../../viewer/viewer';
import { IPersona, IViewConfig } from '../../types';
import { ViewModes } from '../../helpers';
import { Snapshot } from '../ToolUndoRedoManager';
import { Object3DGizmoBinding } from '../modules/transformGizmo/Object3DGizmoBinding';
import { TransformGizmoInteraction } from '../modules/transformGizmo/TransformGizmoInteraction';
import { BoxVolume } from '../../../utils/Volume';
import { Utils } from '../../../utils';

// ─── Snapshot for box transform edits ────────────────────────────

class BoxTransformSnapshot implements Snapshot {
  private position: THREE.Vector3;
  private scale: THREE.Vector3;
  private rotation: THREE.Euler;

  constructor(
    private readonly box: BoxVolume,
    position: THREE.Vector3,
    scale: THREE.Vector3,
    rotation: THREE.Euler,
    private readonly viewer: Viewer,
  ) {
    this.position = position.clone();
    this.scale = scale.clone();
    this.rotation = rotation.clone();
  }

  restore(): Snapshot {
    const currentPosition = this.box.position.clone();
    const currentScale = this.box.scale.clone();
    const currentRotation = this.box.rotation.clone();

    this.box.position.copy(this.position);
    this.box.scale.copy(this.scale);
    this.box.rotation.copy(this.rotation);

    this.position = currentPosition;
    this.scale = currentScale;
    this.rotation = currentRotation;

    this.viewer.scene.dispatchEvent({
      type: 'pointorama_box_moved',
      value: { volume: this.box },
    });

    return this;
  }
}

export class BoxPersonaTool extends BaseTool {
  readonly name = 'box';

  /** The gizmo's link to the edited box: direct TRS mutation, with the box
   *  tool's own undo snapshot + persistence dispatch plugged into the
   *  binding's default lifecycle. */
  private gizmoBinding: Object3DGizmoBinding<BoxVolume>;
  /** Default gizmo choreography (arming, orbit block, undo push, trailing
   *  click) — the tool only delegates its mouse handlers. */
  private gizmoInteraction: TransformGizmoInteraction;
  private currentBox: BoxVolume | undefined;
  private hoveredBox: BoxVolume | undefined;
  private currentBoxHasPosition = false;

  /** Bound handler for volume_removed events */
  private boundOnBoxRemoved: (e: { volume: BoxVolume }) => void;

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);
    this.gizmoBinding = new Object3DGizmoBinding<BoxVolume>({
      createSnapshot: (box) => new BoxTransformSnapshot(box, box.position.clone(), box.scale.clone(), box.rotation.clone(), viewer),
      onCommitted: (box) => {
        this.viewer.scene.dispatchEvent({ type: 'pointorama_box_moved', value: { volume: box } });
      },
    });
    this.gizmoInteraction = new TransformGizmoInteraction({
      viewer,
      undoRedo: () => this.undoRedo ?? null,
      resolveBinding: () => (this.gizmoBinding.getTarget() ? this.gizmoBinding : null),
      // The tool never suppressed the legacy selection pick — keep that.
      manageSelectionSuppression: false,
    });
    this.boundOnBoxRemoved = this.onBoxRemoved.bind(this);
    this.initUndoRedo();
  }

  // ─── Lifecycle ──────────────────────────────────────────────────

  protected onEnable(): void {
    this.gizmoInteraction.attach();
    this.viewer.scene.addEventListener('volume_removed', this.boundOnBoxRemoved);
    this.startInsertion();
  }

  protected onDisable(): void {
    this.cancelBox();
    this.gizmoBinding.setTarget(undefined);
    this.gizmoInteraction.detach();
    this.viewer.scene.removeEventListener('volume_removed', this.boundOnBoxRemoved);
  }

  /**
   * Override view configuration to force 3D perspective view.
   * Boxes should always be visualized in 3D for proper depth perception.
   */
  getViewConfig(): IViewConfig {
    return {
      views: [ViewModes.PERSPECTIVE_3D_RGB],
      defaultViewId: 'perspective_3d_rgb',
    };
  }

  // ─── Input handlers ─────────────────────────────────────────────

  public onMouseMove(event: MouseEvent): void {
    // Over (or dragging) a gizmo handle: the gizmo owns the interaction —
    // no hover retargeting (the handles extend past the box, so re-running
    // the box raycast here would drop the target mid-hover).
    if (this.gizmoInteraction.handleMouseMove(event)) return;

    // Raycast for hovered box
    const ndcMouse = this.getNDCMouse(event);
    const hovered = this.getHoveredBox(ndcMouse);

    if (this.hoveredBox !== hovered) {
      this.gizmoBinding.setTarget(hovered);

      if (this.hoveredBox && !hovered) {
        // Left a box → switch to drop mode (start creating a new one)
        this.switchToDropMode();
      } else if (hovered && !this.hoveredBox) {
        // Entered a box → switch to edit mode (cancel creation)
        this.switchToEditMode();
      }

      this.hoveredBox = hovered;
    }

    // Dangle box from cursor if creating
    if (this.currentBox) {
      this.dangleBoxFromCursor(this.getPixelMouse(event));
    }
  }

  public onMouseDown(event: MouseEvent): void {
    if (this.gizmoInteraction.handleMouseDown(event)) return;
  }

  public onClick(event: MouseEvent): void {
    // Sub-threshold handle click / trailing click of a finished handle drag.
    if (this.gizmoInteraction.handleClick(event)) return;

    const ndcMouse = this.getNDCMouse(event);
    const pixelMouse = this.getPixelMouse(event);

    // Click on existing box → select it
    const box = this.getHoveredBox(ndcMouse);
    if (box) {
      this.gizmoBinding.setTarget(box);
      return;
    }

    // Otherwise → place box
    if (!this.currentBox) return;

    // Position at click point if not already positioned (handles touch flow)
    if (!this.currentBoxHasPosition) {
      this.dangleBoxFromCursor(pixelMouse);
    }
    if (!this.currentBoxHasPosition) return;

    const lastPlaced = this.currentBox;
    this.dropBox();
    this.gizmoBinding.setTarget(lastPlaced);
    this.startInsertion();
  }

  public onDragStart(data: DragEventData): void {
    if (this.gizmoInteraction.handleDragStart(data)) return;
  }

  public onDrag(data: DragEventData): void {
    if (this.gizmoInteraction.handleDrag(data)) return;
  }

  public onDragEnd(data: DragEventData): void {
    if (this.gizmoInteraction.handleDragEnd(data)) return;
  }

  // ─── Box removal listener ──────────────────────────────────────

  private onBoxRemoved(e: { volume: BoxVolume }): void {
    // Clear gizmo target if the removed box was being edited
    if (this.gizmoBinding.getTarget() === e.volume) {
      this.gizmoBinding.setTarget(undefined);
    }
  }

  // ─── Box creation helpers ──────────────────────────────────────

  private startInsertion(): void {
    if (this.currentBox) return;
    this.currentBox = new BoxVolume({ clip: true, visible: true });
    this.currentBoxHasPosition = false;
    this.viewer.scene.addVolume(this.currentBox);
  }

  private dropBox(): void {
    if (!this.currentBox) return;
    this.currentBox.finish();
    this.viewer.scene.dispatchEvent({
      type: 'pointorama_box_placed',
      value: { volume: this.currentBox },
    });
    this.currentBox = undefined;
  }

  private cancelBox(): void {
    if (!this.currentBox) return;
    this.viewer.scene.removeVolume(this.currentBox);
    this.currentBox = undefined;
  }

  private dangleBoxFromCursor(pixelMouse: THREE.Vector2): void {
    if (!this.currentBox) return;
    const camera = this.viewer.scene.getActiveCamera();

    const I = Utils.getMousePointCloudIntersection(pixelMouse, camera, this.viewer, this.viewer.scene.pointclouds, {
      pickClipped: false,
    });

    if (I && 'location' in I) {
      this.currentBox.position.copy(I.location);
      this.currentBoxHasPosition = true;

      const wp = this.currentBox.getWorldPosition(new THREE.Vector3()).applyMatrix4(camera.matrixWorldInverse);
      const w = Math.abs(wp.z / 5);
      this.currentBox.scale.set(w, w, w);
    }
  }

  // ─── Mode switching ─────────────────────────────────────────────

  private switchToEditMode(): void {
    this.cancelBox();
  }

  private switchToDropMode(): void {
    this.startInsertion();
  }

  // ─── Coordinate helpers ─────────────────────────────────────────

  /**
   * Get pixel mouse coordinates (matches InputHandler's this.mouse).
   */
  private getPixelMouse(event: MouseEvent): THREE.Vector2 {
    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    return new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
  }

  /**
   * Get normalized device coordinates for THREE.Raycaster.
   * Uses domElement.width/height to match InputHandler's coordinate system.
   */
  private getNDCMouse(event: MouseEvent): THREE.Vector2 {
    const el = this.viewer.renderer.domElement;
    const rect = el.getBoundingClientRect();
    return new THREE.Vector2(((event.clientX - rect.left) / el.width) * 2 - 1, -((event.clientY - rect.top) / el.height) * 2 + 1);
  }

  /**
   * Raycast against finished, visible boxes to find the one under the cursor.
   * Returns the smallest box (for nested selection).
   */
  private getHoveredBox(ndcMouse: THREE.Vector2): BoxVolume | undefined {
    const camera = this.viewer.scene.getActiveCamera();
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(ndcMouse, camera);
    const boxes = this.viewer.scene.volumes.filter((v: BoxVolume) => v.visible && v.finished);
    const intersects = raycaster
      .intersectObjects(boxes, false)
      .map((intersect) => intersect.object as BoxVolume)
      .sort((a, b) => a.getVolume() - b.getVolume());
    return intersects[0];
  }

  // ─── Cleanup ────────────────────────────────────────────────────

  dispose(): void {
    this.gizmoInteraction.dispose();
    super.dispose();
  }
}
